import { TransactionTableColumns } from "components/Transaction/TransactionsTable";
import {
  FIXED_TRANSACTION_COLUMNS,
  TRANSACTION_COLUMNS,
} from "constants/transaction";
import { TxnAccountType } from "types/Models/books";
import { useLocalStorage } from "./useLocalStorage";

type UseTransactionTableColumnProps = {
  txnAccountType?: TxnAccountType;
  defaultColumns?: (keyof typeof TRANSACTION_COLUMNS)[];
};

const COMMON_COLUMNS = [
  ...FIXED_TRANSACTION_COLUMNS,
  TRANSACTION_COLUMNS.VENDOR,
];

const DEFAULT_STRIPE_COLUMNS = [
  ...COMMON_COLUMNS,
  TRANSACTION_COLUMNS.DAILY_ENTRY,
];

const DEFAULT_OTHER_ACCOUNT_COLUMNS = [
  ...COMMON_COLUMNS,
  TRANSACTION_COLUMNS.FROM,
  TRANSACTION_COLUMNS.SUGGESTED,
];

export default function useTransactionTableColumn({
  txnAccountType,
  defaultColumns,
}: UseTransactionTableColumnProps) {
  const DEFAULT_COLUMNS =
    defaultColumns ||
    (txnAccountType === "STRIPE"
      ? DEFAULT_STRIPE_COLUMNS
      : DEFAULT_OTHER_ACCOUNT_COLUMNS);

  const { localData: values, setItem } =
    useLocalStorage<TransactionTableColumns>(
      `transaction-table-columns-${txnAccountType || "default"}`,
      {
        COLUMNS: DEFAULT_COLUMNS,
      }
    );

  const updateFilter = <S extends keyof TransactionTableColumns>(
    name: S,
    newValue: TransactionTableColumns[S]
  ) => {
    setItem({ ...values, [name]: newValue });
  };

  const getIsShown = (column: keyof typeof TRANSACTION_COLUMNS) => {
    return {
      [column]: values.COLUMNS.includes(column),
    };
  };

  const columnVisibility = Object.values(TRANSACTION_COLUMNS).reduce(
    (acc, column) => {
      acc[column] = values.COLUMNS.includes(column);
      return acc;
    },
    {} as Record<keyof typeof TRANSACTION_COLUMNS, boolean>
  );

  return {
    values,
    updateFilter,
    COLUMNS: DEFAULT_COLUMNS,
    getIsShown,
    TRANSACTION_COLUMNS,
    FIXED_TRANSACTION_COLUMNS,
    columnVisibility,
  };
}
